import { Inject, Injectable, Renderer2, DOCUMENT } from '@angular/core';
import { environment } from 'src/environments/environment';
import _ from 'lodash';
import { AmplitudeService } from './amplitude.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Observable } from 'rxjs';
import { isNativeApp } from '../util/app-platform';

declare let gtag: any;

interface CookieFirstWindow extends Window {
  __CF_PREVENT_AUTO_BANNER_INIT?: boolean;
  __COOKIE_IS_BROWSER_LEGACY?: boolean;
  CF_CUSTOM_INIT_BANNER?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class ScriptService {

  isCookiesScriptInserted: boolean = false;

  constructor(
    private amplitudeService: AmplitudeService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  public loadJsScript(renderer: Renderer2, src: string, async = false): HTMLScriptElement {
    const script = renderer.createElement('script') as HTMLScriptElement;
    script.type = 'text/javascript';
    if(async) script.async = true;
    script.src = src;
    renderer.appendChild(this.document.body, script);
    return script;
  }

  loadScriptsAfterConsent = (e: any, renderer: Renderer2) => {
    const consent: {
      cookiefirst: boolean,
      posthog: boolean,
      google_analytics: boolean,
    } = e.detail || {};

    if(consent.posthog) {
      this.amplitudeService.init(true);
    } else {
      this.amplitudeService.init(false);
    }

    if(consent.google_analytics) {
      this.insertAnalyticsScript(renderer);
    }
  }

  insertCookiesScript(renderer: Renderer2): void {
    if(this.isCookiesScriptInserted || this.isCookieServiceDisabled()) return;

    const isNativePlatform = isNativeApp();
    if(!isNativePlatform && environment.ENVIRONMENT !== 'production') { // enable cookie manager for production environment or native platforms
      this.amplitudeService.init(true);
      return;
    }

    // Execute the callback function when banner sends consent events
    window.addEventListener("cf_services_consent", (e) => this.loadScriptsAfterConsent(e, renderer));
    window.addEventListener("cf_services_consent_loaded", (e) => this.loadScriptsAfterConsent(e, renderer));

    const url = isNativePlatform ? 'localhost' : 'app.llamandoaldoctor.com';
    const src = `https://consent.cookiefirst.com/sites/${url}-${environment.COOKIE_FIRST_KEY}/consent.js`;
    const cookieFirstWindow = window as CookieFirstWindow;
    if (isNativePlatform) {
      // CookieFirst misreads WebView user agents as legacy browsers. Defer initialization
      // so its legacy loader cannot call document.write() from this dynamic script.
      cookieFirstWindow.__CF_PREVENT_AUTO_BANNER_INIT = true;
    }

    const script = this.loadJsScript(renderer, src);
    if (isNativePlatform) {
      script.addEventListener(
        'load',
        () => {
          cookieFirstWindow.__CF_PREVENT_AUTO_BANNER_INIT = false;
          cookieFirstWindow.__COOKIE_IS_BROWSER_LEGACY = false;
          if (cookieFirstWindow.CF_CUSTOM_INIT_BANNER) cookieFirstWindow.CF_CUSTOM_INIT_BANNER();
        },
        { once: true }
      );
    }
    this.isCookiesScriptInserted = true;
  }

  private isCookieServiceDisabled(): boolean {
    /* Illow was our previous cookie provider */
    let disableIllow: boolean | null = localStorage.getItem('disableIllow') === 'true' || null;

    if(_.isNil(disableIllow)) {
      disableIllow = new URLSearchParams(window.location.search)?.get('disableIllow') === 'true';
    }

    return disableIllow;
  }

  insertAnalyticsScript(renderer: Renderer2): void {
    const src = `https://www.googletagmanager.com/gtag/js?id=${environment.ANALYTICS_ID}`;
    this.loadJsScript(renderer, src, true);

    this.setUpAnalytics();
  }

  setUpAnalytics(): void {
    const navEvents$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ) as Observable<NavigationEnd>;
    navEvents$.subscribe((event: NavigationEnd) => {
      gtag('config', environment.ANALYTICS_ID, {
        page_path: event.urlAfterRedirects
      });
    });
  }
}
